import React from 'react';
import { Plus, Edit, Trash2, Search, TrendingUp, Paperclip, Eye } from 'lucide-react';
import { useCrud, useModal, useForm } from '../hooks';
import { movimentacaoCaixaService, caixaService, planoContaService } from '../services/api';

const MovimentacoesCaixa = () => {
  const { data: movimentacoes, loading, error, create, update, remove, refresh } = useCrud(movimentacaoCaixaService, 'movimentações de caixa');
  const { data: caixas } = useCrud(caixaService, 'caixas');
  const { data: planosContas } = useCrud(planoContaService, 'planos de contas');
  const [caixasAbertos, setCaixasAbertos] = React.useState([]);
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [anexoPreview, setAnexoPreview] = React.useState(null);
  const modal = useModal();
  const [searchTerm, setSearchTerm] = React.useState('');

  // Carregar caixas abertos quando o modal for aberto
  React.useEffect(() => {
    const loadCaixasAbertos = async () => {
      if (modal.isOpen) {
        try {
          const response = await caixaService.getAbertos();
          setCaixasAbertos(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
          console.error('Erro ao carregar caixas abertos:', error);
          setCaixasAbertos([]);
        }
      }
    };
    
    loadCaixasAbertos();
  }, [modal.isOpen]);

  const validationRules = {
    caixa_id: { required: true, label: 'Caixa' },
    plano_conta_id: { required: false, label: 'Plano de Conta' },
    valor: { 
      required: true, 
      label: 'Valor',
      custom: (value) => {
        if (!value || isNaN(parseFloat(value))) {
          return 'Valor deve ser um número válido';
        }
        return null;
      }
    },
    data: { required: true, label: 'Data' },
    descricao: { required: false, label: 'Descrição' }
  };

  const form = useForm(
    { 
      caixa_id: '',
      plano_conta_id: '',
      valor: '',
      data: new Date().toISOString().split('T')[0],
      descricao: ''
    },
    validationRules
  );

  const filteredMovimentacoes = movimentacoes.filter(mov =>
    mov.descricao?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    mov.Caixa?.responsavel_abertura?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    mov.PlanoConta?.nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreate = async () => {
    // Carregar caixas abertos antes de abrir o modal
    try {
      const response = await caixaService.getAbertos();
      const caixasData = Array.isArray(response.data) ? response.data : [];
      setCaixasAbertos(caixasData);
      
      if (caixasData.length === 0) {
        alert('Não há caixas abertos disponíveis para registrar movimentações. Abra um caixa primeiro.');
        return;
      }
      
      form.reset();
      setSelectedFile(null);
      setAnexoPreview(null);
      modal.open({ mode: 'create', title: 'Nova Movimentação' });
    } catch (error) {
      console.error('Erro ao carregar caixas abertos:', error);
      alert('Erro ao verificar caixas disponíveis. Tente novamente.');
    }
  };

  const handleEdit = (movimentacao) => {
    form.reset({
      caixa_id: movimentacao.caixa_id || '',
      plano_conta_id: movimentacao.plano_conta_id || '',
      valor: movimentacao.valor || '',
      data: movimentacao.data ? movimentacao.data.split('T')[0] : new Date().toISOString().split('T')[0],
      descricao: movimentacao.descricao || ''
    });
    setSelectedFile(null);
    setAnexoPreview(null);
    modal.open({ mode: 'edit', title: 'Editar Movimentação', id: movimentacao.id });
  };

  const handleDelete = async (movimentacao) => {
    if (window.confirm(`Deseja realmente excluir esta movimentação?`)) {
      const result = await remove(movimentacao.id);
      if (result.success) {
        // Success handled by the hook
      }
    }
  };

  // Função para lidar com seleção de arquivo
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar tipo de arquivo
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        alert('Tipo de arquivo não permitido. Aceitos: PDF, JPEG, PNG');
        event.target.value = '';
        return;
      }
      
      // Validar tamanho (10MB máximo)
      if (file.size > 10 * 1024 * 1024) {
        alert('Arquivo muito grande. Tamanho máximo: 10MB');
        event.target.value = '';
        return;
      }
      
      setSelectedFile(file);
    } else {
      setSelectedFile(null);
    }
  };

  // Função para visualizar anexo
  const handleViewAnexo = async (movimentacaoId) => {
    try {
      const response = await movimentacaoCaixaService.getAnexo(movimentacaoId);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      
      // Abrir em nova janela
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        // Fallback para download se popup foi bloqueado
        const link = document.createElement('a');
        link.href = url;
        link.download = `anexo-movimentacao-${movimentacaoId}`;
        link.click();
      }
      
      // Limpar URL após uso
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Erro ao buscar anexo:', error);
      alert('Erro ao carregar anexo');
    }
  };

  const handleSubmit = async (values) => {
    // Limpar campos opcionais vazios
    const cleanedValues = { ...values };
    
    if (!cleanedValues.caixa_id || cleanedValues.caixa_id === '') {
      cleanedValues.caixa_id = null;
    }
    if (!cleanedValues.plano_conta_id || cleanedValues.plano_conta_id === '') {
      cleanedValues.plano_conta_id = null;
    }

    let result;
    try {
      if (modal.data.mode === 'create') {
        result = selectedFile 
          ? await movimentacaoCaixaService.createWithFile(cleanedValues, selectedFile)
          : await create(cleanedValues);
      } else {
        result = selectedFile 
          ? await movimentacaoCaixaService.updateWithFile(modal.data.id, cleanedValues, selectedFile)
          : await update(modal.data.id, cleanedValues);
      }
      
      console.log('Result from API:', result);
      
      // Verificar se foi bem-sucedido (diferentes estruturas de resposta)
      const isSuccess = result?.success !== false && result?.data !== undefined;
      
      if (isSuccess || result?.status === 200 || result?.status === 201) {
        // Limpar arquivo selecionado
        setSelectedFile(null);
        setAnexoPreview(null);
        modal.close();
        // Refresh data usando o hook
        await refresh();
      } else {
        console.error('Erro na resposta:', result);
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
    }

    if (result.success) {
      modal.close();
      form.reset();
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Movimentações de Caixa</h1>
        <button onClick={handleCreate} className="btn btn-primary">
          <Plus size={20} />
          Nova Movimentação
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="card mb-6">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar movimentações..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input pl-10"
            />
          </div>
        </div>
      </div>

      <div className="card">
        {filteredMovimentacoes.length === 0 ? (
          <div className="empty-state">
            <TrendingUp className="empty-state-icon" />
            <h3 className="text-lg font-medium mb-2">Nenhuma movimentação encontrada</h3>
            <p className="mb-4">
              {searchTerm ? 'Tente ajustar os filtros de busca.' : 'Comece criando uma nova movimentação.'}
            </p>
            {!searchTerm && (
              <button onClick={handleCreate} className="btn btn-primary">
                <Plus size={20} />
                Nova Movimentação
              </button>
            )}
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Caixa</th>
                  <th>Plano de Conta</th>
                  <th>Descrição</th>
                  <th>Valor</th>
                  <th>Anexo</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredMovimentacoes.map((movimentacao) => (
                  <tr key={movimentacao.id}>
                    <td>{formatDate(movimentacao.data)}</td>
                    <td>{movimentacao.Caixa?.responsavel_abertura || 'N/A'}</td>
                    <td>{movimentacao.PlanoConta?.nome || 'N/A'}</td>
                    <td>{movimentacao.descricao || '-'}</td>
                    <td className={parseFloat(movimentacao.valor) >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {formatCurrency(movimentacao.valor)}
                    </td>
                    <td>
                      {movimentacao.tem_anexo ? (
                        <button
                          onClick={() => handleViewAnexo(movimentacao.id)}
                          className="btn btn-sm btn-info flex items-center gap-1"
                          title="Visualizar anexo"
                        >
                          <Paperclip size={14} />
                          <Eye size={14} />
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(movimentacao)}
                          className="btn btn-sm btn-secondary"
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(movimentacao)}
                          className="btn btn-sm btn-danger"
                          title="Excluir"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {modal.isOpen && (
        <div className="modal-overlay" onClick={modal.close}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{modal.data.title}</h2>
              <button onClick={modal.close} className="modal-close">×</button>
            </div>

            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="form-group">
                <label className="form-label">Data *</label>
                <input
                  type="date"
                  value={form.values.data}
                  onChange={(e) => form.setValue('data', e.target.value)}
                  onBlur={() => form.setTouched('data')}
                  className={`form-input ${form.errors.data && form.touched.data ? 'border-red-500' : ''}`}
                />
                {form.errors.data && form.touched.data && (
                  <div className="form-error">{form.errors.data}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Caixa *</label>
                <select
                  value={form.values.caixa_id}
                  onChange={(e) => form.setValue('caixa_id', e.target.value)}
                  onBlur={() => form.setTouched('caixa_id')}
                  className={`form-select ${form.errors.caixa_id && form.touched.caixa_id ? 'border-red-500' : ''}`}
                >
                  <option value="">Selecione um caixa aberto</option>
                  {Array.isArray(caixasAbertos) && caixasAbertos.map((caixa) => (
                    <option key={caixa.id} value={caixa.id}>
                      {caixa.responsavel_abertura} - {formatDate(caixa.data_abertura)} - {caixa.Evento?.nome || 'Sem evento'}
                    </option>
                  ))}
                </select>
                {Array.isArray(caixasAbertos) && caixasAbertos.length === 0 && (
                  <div className="text-amber-600 text-sm mt-1">
                    ⚠️ Não há caixas abertos disponíveis para movimentação
                  </div>
                )}
                {form.errors.caixa_id && form.touched.caixa_id && (
                  <div className="form-error">{form.errors.caixa_id}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Plano de Conta</label>
                <select
                  value={form.values.plano_conta_id}
                  onChange={(e) => form.setValue('plano_conta_id', e.target.value)}
                  onBlur={() => form.setTouched('plano_conta_id')}
                  className={`form-select ${form.errors.plano_conta_id && form.touched.plano_conta_id ? 'border-red-500' : ''}`}
                >
                  <option value="">Selecione um plano de conta</option>
                  {planosContas.map((plano) => (
                    <option key={plano.id} value={plano.id}>
                      {plano.nome} ({plano.tipo})
                    </option>
                  ))}
                </select>
                {form.errors.plano_conta_id && form.touched.plano_conta_id && (
                  <div className="form-error">{form.errors.plano_conta_id}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Valor *</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.values.valor}
                  onChange={(e) => form.setValue('valor', e.target.value)}
                  onBlur={() => form.setTouched('valor')}
                  className={`form-input ${form.errors.valor && form.touched.valor ? 'border-red-500' : ''}`}
                  placeholder="0.00"
                />
                {form.errors.valor && form.touched.valor && (
                  <div className="form-error">{form.errors.valor}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Descrição</label>
                <textarea
                  value={form.values.descricao}
                  onChange={(e) => form.setValue('descricao', e.target.value)}
                  className="form-input"
                  placeholder="Digite uma descrição para a movimentação"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Anexo (opcional)</label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="form-input"
                />
                <div className="text-sm text-gray-500 mt-1">
                  Formatos aceitos: PDF, JPEG, PNG (máximo 10MB)
                </div>
                {selectedFile && (
                  <div className="mt-2 p-2 bg-blue-50 rounded flex items-center gap-2">
                    <Paperclip size={14} />
                    <span className="text-sm">{selectedFile.name}</span>
                    <button
                      type="button"
                      onClick={() => setSelectedFile(null)}
                      className="text-red-500 hover:text-red-700 ml-auto"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" onClick={modal.close} className="btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {modal.data.mode === 'create' ? 'Criar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MovimentacoesCaixa;